import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Lightweight lazy image that sets src only when visible (for thumbnails)
function LazyImg({ src, alt = '', className = '', style = {}, onLoad, onError, loading = 'lazy', decoding = 'async', eager = false }) {
    const imgRef = useRef(null);
    const [inView, setInView] = useState(eager); // If eager=true, load immediately
    useEffect(() => {
        if (eager) {
            setInView(true);
            return;
        }
        
        const el = imgRef.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    setInView(true);
                }
            });
        }, { rootMargin: '120px' });
        obs.observe(el);
        return () => {
            try { obs.unobserve(el); } catch { }
            try { obs.disconnect(); } catch { }
        };
    }, [eager]);
    return (
        <img
            ref={imgRef}
            src={inView ? src : undefined}
            data-src={src}
            alt={alt}
            className={className}
            style={style}
            loading={loading}
            decoding={decoding}
            onLoad={onLoad}
            onError={onError}
        />
    );
}

function ProductImageGallery({ images = [], initialIndex = 0, name, hideThumbs = false, transparentBackground = false }) {
    const [activeIdx, setActiveIdx] = useState(initialIndex);
    // Keep active index in sync when the caller changes the initialIndex (e.g., primary image changes)
    React.useEffect(() => {
        if (typeof initialIndex === 'number' && initialIndex >= 0 && initialIndex < images.length) {
            setActiveIdx(initialIndex);
        } else if (images.length > 0 && activeIdx >= images.length) {
            setActiveIdx(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialIndex, images]);
    const [zoomed, setZoomed] = useState(false);
    const mainImgRef = useRef(null);

    const handlePrev = () => setActiveIdx(idx => (idx > 0 ? idx - 1 : images.length - 1));
    const handleNext = () => setActiveIdx(idx => (idx < images.length - 1 ? idx + 1 : 0));
    const handleThumbClick = idx => setActiveIdx(idx);

    const activeImage = images[activeIdx] || images[0] || {};
    const mainSrc = activeImage?.image_url || '';
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imageCache, setImageCache] = useState(new Set());
    
    // Preload all images immediately when component mounts or images change
    React.useEffect(() => {
        if (!Array.isArray(images) || images.length === 0) return;
        
        const preloadPromises = images.map(img => {
            const url = img?.image_url;
            if (!url || imageCache.has(url)) return Promise.resolve();
            
            return new Promise((resolve) => {
                const image = new Image();
                image.onload = () => {
                    setImageCache(prev => new Set([...prev, url]));
                    resolve();
                };
                image.onerror = () => resolve(); // Don't fail the batch
                image.src = url;
            });
        });
        
        Promise.all(preloadPromises);
    }, [images]);
    
    React.useEffect(() => {
        if (!mainSrc) {
            setImgLoaded(true);
            return;
        }
        
        // If image is already cached, show immediately
        if (imageCache.has(mainSrc)) {
            setImgLoaded(true);
            return;
        }
        
        // Otherwise check if DOM image is already complete
        setImgLoaded(false);
        const imgEl = mainImgRef.current;
        if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
            setImgLoaded(true);
            setImageCache(prev => new Set([...prev, mainSrc]));
        }
    }, [mainSrc, imageCache]);

    // Preload all other images for the same variant in the background
    const preloadedRef = React.useRef(new Set());
    React.useEffect(() => {
        if (!Array.isArray(images) || images.length <= 1) return;
        let cancelled = false;
        const urls = images.map(i => i?.image_url).filter(Boolean);
        const seen = preloadedRef.current;
        const loadOne = (url) => {
            if (!url || seen.has(url) || cancelled) return;
            seen.add(url);
            try {
                const im = new Image();
                im.decoding = 'async';
                im.loading = 'lazy';
                im.src = url;
            } catch { }
        };
        const kick = () => {
            // Stagger a bit to avoid a burst; still very fast overall
            urls.forEach((u, idx) => setTimeout(() => loadOne(u), idx * 30));
        };
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            window.requestIdleCallback(() => kick(), { timeout: 1500 });
        } else {
            setTimeout(kick, 120);
        }
        return () => { cancelled = true; };
    }, [images]);

    // Responsive: vertical on desktop, horizontal on mobile
    // Lightbox/zoom: simple overlay with larger image
    return (
        <div className="w-full flex flex-col md:flex-row items-center md:items-start gap-4">
            {/* Thumbnails (optional) */}
            {!hideThumbs && (
                <div className="flex md:flex-col flex-row md:gap-2 gap-2 md:mr-4 md:overflow-visible overflow-x-auto md:overflow-x-hidden md:h-auto" style={{ maxHeight: 'none' }}>
                    {images.slice(0, 10).map((img, idx, arr) => (
                        <button
                            key={img.image_url + idx}
                            onClick={() => handleThumbClick(idx)}
                            onMouseEnter={() => handleThumbClick(idx)}
                            className={`border-2 rounded mb-1 flex-shrink-0 overflow-hidden transition-all ${idx === activeIdx ? 'border-cyan-400' : 'border-white/20'}`}
                            style={{
                                outline: idx === activeIdx ? '2px solid #22d3ee' : 'none',
                                width: `calc(100% / ${arr.length > 5 ? Math.min(arr.length, 10) : 5})`,
                                height: 'auto',
                                aspectRatio: '1/1',
                                maxWidth: '64px',
                                minWidth: '40px',
                                maxHeight: '64px',
                                minHeight: '40px',
                                background: transparentBackground ? 'rgba(255,255,255,0.1)' : 'rgba(26,35,50,0.35)'
                            }}
                            aria-label={`Show image ${idx + 1}`}
                        >
                            <LazyImg
                                src={img.image_url}
                                alt={name + ' thumbnail ' + (idx + 1)}
                                className="w-full h-full object-cover"
                                style={{ background: transparentBackground ? 'transparent' : 'rgba(26,35,50,0.2)' }}
                                loading="eager"
                                decoding="async"
                                eager={true}
                                onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                            />
                        </button>
                    ))}
                </div>
            )}
            {/* Main Image + Arrows */}
            <div className="relative flex flex-col items-center justify-center w-full">
                <div className="relative w-full aspect-square max-w-lg mx-auto group overflow-hidden rounded-lg">
                    {/* Show skeleton only for non-cached images */}
                    {!imgLoaded && !imageCache.has(mainSrc) && (
                        <div className="absolute inset-0 rounded-lg skeleton" aria-hidden />
                    )}
                    <AnimatePresence mode="wait">
                        {mainSrc ? (
                            <motion.img
                                key={mainSrc}
                                ref={mainImgRef}
                                src={mainSrc}
                                alt={name}
                                className="block w-full h-full object-contain rounded-lg shadow-lg cursor-zoom-in"
                                style={{ background: transparentBackground ? 'transparent' : 'rgba(255,255,255,0.6)' }}
                                decoding="async"
                                loading="eager"
                                fetchpriority="high"
                                initial={{ 
                                    opacity: imageCache.has(mainSrc) ? 1 : 0, 
                                    scale: imageCache.has(mainSrc) ? 1 : 0.995 
                                }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.995 }}
                                transition={{ 
                                    duration: imageCache.has(mainSrc) ? 0.05 : 0.15, 
                                    ease: 'easeOut' 
                                }}
                                onClick={() => setZoomed(true)}
                                onLoad={() => {
                                    setImgLoaded(true);
                                    setImageCache(prev => new Set([...prev, mainSrc]));
                                }}
                                onError={(e) => { 
                                    e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; 
                                    setImgLoaded(true); 
                                }}
                            />
                        ) : null}
                    </AnimatePresence>
                    {/* Left Arrow */}
                    {images.length > 1 && (
                        <button
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-cyan-600/80 text-white rounded-full p-2 z-10"
                            onClick={handlePrev}
                            aria-label="Previous image"
                            style={{ backdropFilter: 'blur(2px)' }}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>
                    )}
                    {/* Right Arrow */}
                    {images.length > 1 && (
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-cyan-600/80 text-white rounded-full p-2 z-10"
                            onClick={handleNext}
                            aria-label="Next image"
                            style={{ backdropFilter: 'blur(2px)' }}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    )}
                </div>
                {/* Bottom arrows */}
                {images.length > 1 && (
                    <div className="mt-2 flex items-center justify-center gap-3">
                        <button
                            className="px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs"
                            onClick={handlePrev}
                            aria-label="Previous image"
                        >
                            Prev
                        </button>
                        <button
                            className="px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs"
                            onClick={handleNext}
                            aria-label="Next image"
                        >
                            Next
                        </button>
                    </div>
                )}
                {/* Lightbox/Zoom Overlay */}
                {zoomed && (
                    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50" onClick={() => setZoomed(false)}>
                        <img
                            src={activeImage.image_url}
                            alt={name}
                            className="max-w-full max-h-full rounded-lg shadow-2xl border-4 border-cyan-400 opacity-0"
                            style={{ cursor: 'zoom-out', background: 'transparent' }}
                            loading="eager"
                            decoding="async"
                            onLoad={(e) => { e.currentTarget.style.opacity = 1; }}
                            onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                        />
                        <button
                            className="absolute top-6 right-8 text-white text-3xl font-bold bg-black/60 rounded-full px-3 py-1"
                            onClick={e => { e.stopPropagation(); setZoomed(false); }}
                            aria-label="Close zoom"
                        >
                            &times;
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductImageGallery;


